// Owns Socket.IO connection, room-state, and session lifecycle subscriptions.
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { getSocket } from '../socket';
import type { Color, Role, RoomMode, RoomState } from '../types';
import { boardFromFen, startBoard, type BoardMap, type PieceCode } from '../board/pieces';
import type { PendingPromotion } from './lectureActions';

type Screen = 'home' | 'workspace';
type StateSetter<T> = Dispatch<SetStateAction<T>>;
type ClockSync = { serverNow: number; localMonotonicAtSync: number } | null;

type LifecycleBindings = {
  refs: {
    roomId: { current: string };
    role: { current: Role };
    lastRestoredSocketId: { current: string | null };
    roomState: { current: RoomState | null };
  };
  state: {
    setState: StateSetter<RoomState | null>;
    setRoomId: StateSetter<string>;
    setSelectedMode: StateSetter<RoomMode>;
    setStartingTurn: StateSetter<Color>;
    setTrainerColor: StateSetter<Color>;
    setBoard: StateSetter<BoardMap>;
    setReviewHistory: StateSetter<RoomState['history']>;
    setReviewIndex: StateSetter<number>;
    setLectureClockSync: StateSetter<ClockSync>;
    setGameClockSync: StateSetter<ClockSync>;
    setExerciseIndex: StateSetter<number>;
    setExerciseFrozen: StateSetter<boolean>;
    setAttempts: StateSetter<string[]>;
    setStudentExerciseFen: StateSetter<string>;
    setSelected: StateSetter<string | null>;
    setSpare: StateSetter<PieceCode | null>;
    setRole: StateSetter<Role>;
    setName: StateSetter<string>;
    setScreen: StateSetter<Screen>;
    setStatus: StateSetter<string>;
    setConnected: StateSetter<boolean>;
    setStudentList: StateSetter<Record<string, string>>;
    setStudentEvents: StateSetter<string[]>;
    setStudentMoveHistory: StateSetter<Array<{ studentName: string; move: string; isCorrect: boolean }>>;
    setAnimationVersion: StateSetter<number>;
    setPendingPromotion: StateSetter<PendingPromotion | null>;
    setRecording: StateSetter<boolean>;
    setGameOver: StateSetter<{ winner: Color | 'draw' | null; reason: string | null } | null>;
    setMonotonicNow: StateSetter<number>;
    setPendingGameMove: StateSetter<boolean>;
  };
};

const socket = getSocket();
const DEFAULT_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function gameResultStatus(winner: Color | 'draw' | null, reason: string | null) {
  const title =
    winner === 'w' ? 'Победа белых' :
    winner === 'b' ? 'Победа чёрных' :
    winner === 'draw' ? 'Ничья' :
    'Игра окончена';
  return title + (reason ? ': ' + reason : '');
}

export function useRoomSocketLifecycle({ refs, state }: LifecycleBindings) {
  const {
    roomId: roomIdRef,
    role: roleRef,
    lastRestoredSocketId: lastRestoredSocketIdRef,
    roomState: roomStateRef
  } = refs;
  const {
    setState, setRoomId, setSelectedMode, setStartingTurn, setTrainerColor, setBoard,
    setReviewHistory, setReviewIndex, setLectureClockSync, setGameClockSync,
    setExerciseIndex, setExerciseFrozen, setAttempts, setStudentExerciseFen,
    setSelected, setSpare, setRole, setName, setScreen, setStatus, setConnected,
    setStudentList, setStudentEvents, setStudentMoveHistory, setAnimationVersion,
    setPendingPromotion, setRecording, setGameOver, setMonotonicNow, setPendingGameMove
  } = state;

  const restoreAuthoritativeBoard = () => {
    const current = roomStateRef.current;
    if (current) setBoard(boardFromFen(current.fen));
    setPendingGameMove(false);
  };

  useEffect(() => {
    const applyState = (next: RoomState, id?: string) => {
      setState(next);
      if (id) setRoomId(id);
      setSelectedMode(next.mode);
      setStartingTurn(next.turn);
      if (roleRef.current === 'trainer' && next.gameTimer.active) {
        setTrainerColor(next.trainerColor);
      }
      setBoard(boardFromFen(next.fen));
      setPendingGameMove(false);
      setReviewHistory(next.reviewHistory || []);
      setReviewIndex(-1);
      const localMonotonicAtSync = performance.now();
      setLectureClockSync({ serverNow: next.lectureTimer.serverNow, localMonotonicAtSync });
      setGameClockSync({ serverNow: next.gameTimer.serverNow, localMonotonicAtSync });

      if (sessionStorage.getItem('pvl-role') === 'student') {
        setExerciseIndex(0);
        setExerciseFrozen(false);
        setAttempts([]);
        setStudentExerciseFen(next.exercise?.initialFen || next.fen);
      }

      setSelected(null);
      setSpare(null);
    };

    const reconnect = () => {
      if (!socket.id || socket.id === lastRestoredSocketIdRef.current) return;
      lastRestoredSocketIdRef.current = socket.id;

      const savedRole = sessionStorage.getItem('pvl-role') as Role | null;
      const savedRoom = sessionStorage.getItem('pvl-room');
      const savedName = sessionStorage.getItem('pvl-name') || '';
      if (!savedRole || !savedRoom) return;

      if (savedRole !== 'trainer' && savedRole !== 'student') {
        sessionStorage.clear();
        return;
      }
      setRole(savedRole);
      setName(savedName);
      setScreen('workspace');
      if (savedRole === 'trainer') {
        socket.emit('rejoinTrainer', {
          roomId: savedRoom,
          trainerToken: sessionStorage.getItem('pvl-trainer-token') || ''
        });
      }
      else socket.emit('joinRoom', {
        roomId: savedRoom,
        studentName: savedName || 'Ученик',
        studentToken: sessionStorage.getItem('pvl-student-token') || undefined
      });
    };

    const onConnect = () => {
      setConnected(true);
      if (!sessionStorage.getItem('pvl-room')) setStatus('Готово');
      reconnect();
    };
    const onDisconnect = () => {
      restoreAuthoritativeBoard();
      setConnected(false);
      setStatus('Соединение потеряно. Повторное подключение…');
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) onConnect();

    socket.on('roomCreated', (data) => {
      if (sessionStorage.getItem('pvl-role') === 'trainer') {
        setStudentList({});
        setStudentEvents([]);
        setStudentMoveHistory([]);
      }
      sessionStorage.setItem('pvl-role', 'trainer');
      sessionStorage.setItem('pvl-room', data.roomId);
      if (data.trainerToken) sessionStorage.setItem('pvl-trainer-token', data.trainerToken);
      sessionStorage.setItem('pvl-name', sessionStorage.getItem('pvl-name') || 'Тренер');
      applyState(data.state, data.roomId);
      setRole('trainer');
      setScreen('workspace');
      setStatus('Комната ' + data.roomId + ' готова');
    });

    socket.on('joinedRoom', (data) => {
      applyState(data.state, data.roomId);
      if (data.studentToken) sessionStorage.setItem('pvl-student-token', data.studentToken);

      if (data.studentExercise) {
        setExerciseIndex(data.studentExercise.index);
        setExerciseFrozen(data.studentExercise.frozen);
        setAttempts([]);
        setStudentExerciseFen(data.studentExercise.fen);

        if (data.state.exercise) {
          setBoard(boardFromFen(data.studentExercise.fen));
        }
      } else {
        setStudentExerciseFen(data.state.exercise?.initialFen || data.state.fen);
      }

      setRole('student');
      setScreen('workspace');
      setStatus((data.studentExercise?.index ?? 0) > 0
        ? 'Состояние задачи восстановлено'
        : 'Подключено к ' + data.roomId);
    });

    socket.on('gameStateUpdate', (next) => {
      const viewerRole = roleRef.current;
      if (next.lastMoveBy && next.lastMoveBy !== viewerRole) {
        setAnimationVersion((version) => version + 1);
      }
      setState(next);
      setSelectedMode(next.mode);
      setStartingTurn(next.turn);
      if (roleRef.current === 'trainer' && next.gameTimer.active) {
        setTrainerColor(next.trainerColor);
      }
      setBoard(boardFromFen(next.fen));
      setPendingGameMove(false);
      setReviewHistory(next.reviewHistory || []);
      setReviewIndex(-1);
      const localMonotonicAtSync = performance.now();
      setLectureClockSync({ serverNow: next.lectureTimer.serverNow, localMonotonicAtSync });
      setGameClockSync({ serverNow: next.gameTimer.serverNow, localMonotonicAtSync });

      if (sessionStorage.getItem('pvl-role') === 'student') {
        setExerciseIndex(0);
        setExerciseFrozen(false);
        setStudentExerciseFen(next.exercise?.initialFen || next.fen);
      }

      setSelected(null);
      setSpare(null);
      setPendingPromotion(null);
      setStatus(next.mode === 'lecture' ? 'Режим лекции' : 'Режим партии: ' + next.mode);
    });

    socket.on('boardUpdate', ({ fen, turn }) => {
      if (roleRef.current === 'student') {
        setAnimationVersion((version) => version + 1);
      }
      setBoard(boardFromFen(fen));
      setPendingGameMove(false);
      setState((prev) => prev ? { ...prev, fen, turn } : prev);
      setSelected(null);
    });

    socket.on('exerciseUpdate', ({ exercise }) => {
      setState((prev) => prev ? { ...prev, exercise } : prev);
      const currentRole = sessionStorage.getItem('pvl-role');
      if (currentRole === 'student') {
        if (exercise) {
          setBoard(boardFromFen(exercise.initialFen));
          setStudentExerciseFen(exercise.initialFen);
          setExerciseIndex(0);
          setExerciseFrozen(false);
          setAttempts([]);
        } else {
          setStudentExerciseFen(DEFAULT_FEN);
          setExerciseIndex(0);
          setExerciseFrozen(false);
        }
        setSelected(null);
      }
      setStatus(exercise ? 'Упражнение загружено' : 'Упражнение очищено');
    });

    socket.on('lectureTaskReset', ({ exercise, reason }) => {
      setState((prev) => prev ? { ...prev, exercise } : prev);
      const currentRole = sessionStorage.getItem('pvl-role');
      if (currentRole !== 'student') return;
      if (exercise) {
        setBoard(boardFromFen(exercise.initialFen));
        setStudentExerciseFen(exercise.initialFen);
      } else {
        setStudentExerciseFen(DEFAULT_FEN);
      }
      setExerciseIndex(0);
      setExerciseFrozen(false);
      setAttempts([]);
      setSelected(null);
      setStatus(reason === 'timer-expired' ? 'Время истекло. Задача сброшена.' : 'Таймер остановлен. Задача сброшена.');
    });

    socket.on('exerciseResult', (result) => {
      if (sessionStorage.getItem('pvl-role') !== 'student') return;

      if (result.fen) {
        if (result.systemResponse) {
          setAnimationVersion((version) => version + 1);
        }
        setBoard(boardFromFen(result.fen));
        setStudentExerciseFen(result.fen);
      }
      setExerciseIndex(result.index);
      setExerciseFrozen(Boolean(result.pendingSystemResponse) || result.completed);
      setSelected(null);

      if (result.systemResponse) {
        setStatus(result.completed ? 'Задача решена!' : 'Ответ системы');
        return;
      }

      setAttempts((prev) => [
        (result.correct ? '✓ ' : '✗ ') + result.actualSan,
        ...prev
      ].slice(0, 50));

      if (result.correct) {
        setStatus(result.pendingSystemResponse ? 'Верно. Ответ системы…' : (result.completed ? 'Задача решена!' : 'Верно'));
      } else {
        setExerciseFrozen(true);
        setStatus('Неверно. Дождитесь повторного запуска задачи тренером.');
      }
    });

    socket.on('exerciseMoveRejected', ({ reason }) => {
      if (sessionStorage.getItem('pvl-role') === 'student') {
        setSelected(null);
        setStatus(reason);
      }
    });

    socket.on('lectureTimerUpdate', (timer) => {
      setLectureClockSync({ serverNow: timer.serverNow, localMonotonicAtSync: performance.now() });
      setState((prev) => prev ? { ...prev, lectureTimer: timer } : prev);
    });

    socket.on('studentJoined', (data) => {
      setStudentList((prev) => ({ ...prev, [data.studentId]: data.studentName }));
      setStudentEvents((prev) => ['→ ' + data.studentName + ' подключился', ...prev].slice(0, 100));
    });

    socket.on('studentLeft', (data) => {
      setStudentList((prev) => {
        const next = { ...prev };
        delete next[data.studentId];
        return next;
      });
      setStudentEvents((prev) => ['← ' + data.studentName + ' отключился', ...prev].slice(0, 100));
    });

    socket.on('studentMove', (data) => {
      setStudentMoveHistory((prev) => [data, ...prev].slice(0, 100));
    });

    socket.on('gameHistoryForReview', (data) => {
      setReviewHistory(data.history || []);
      setReviewIndex(-1);
    });

    socket.on('updateGraphics', (data) => {
      setState((prev) => prev ? { ...prev, arrows: data.arrows, highlights: data.highlights } : prev);
    });

    socket.on('drawArrow', (arrow) => {
      setState((prev) => prev ? { ...prev, arrows: [...prev.arrows, arrow] } : prev);
    });

    socket.on('moveUndone', () => setStatus('Последний ход отменён'));
    socket.on('gameOver', (data) => {
      setStatus(gameResultStatus(data.winner, data.reason));
      setGameOver(data);
    });
    socket.on('gameMoveRejected', (data) => {
      restoreAuthoritativeBoard();
      setStatus(data.reason);
    });
    socket.on('serverError', (data) => {
      restoreAuthoritativeBoard();
      setStatus(data.message);
      if (data.code === 'SESSION_REPLACED') {
        sessionStorage.removeItem('pvl-room');
        sessionStorage.removeItem('pvl-student-token');
        sessionStorage.removeItem('pvl-trainer-token');
        setRoomId('');
        setState(null);
        setBoard(startBoard());
        setScreen('home');
        return;
      }

      if (data.code === 'ROOM_NOT_FOUND' || data.code === 'TRAINER_AUTH_FAILED') {
        sessionStorage.removeItem('pvl-room');
        sessionStorage.removeItem('pvl-trainer-token');
        sessionStorage.removeItem('pvl-student-token');
        setRoomId('');
        setState(null);
        setBoard(startBoard());
        setSelected(null);
        setSpare(null);
        setRecording(false);
        setPendingPromotion(null);
        setReviewHistory([]);
        setExerciseIndex(0);
        setExerciseFrozen(false);
        setAttempts([]);
        setStudentExerciseFen(DEFAULT_FEN);
        setStudentList({});
        setStudentEvents([]);
        setStudentMoveHistory([]);
        setGameOver(null);
        setScreen('home');
      }
    });
    socket.on('authError', (message) => setStatus(message));

    const timer = window.setInterval(() => setMonotonicNow(performance.now()), 100);

    let clearGraphicsTimer: number | null = null;
    let didLongClear = false;

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (event.button !== 2) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('.board-wrap')) return;

      didLongClear = false;
      clearGraphicsTimer = window.setTimeout(() => {
        if (roleRef.current === 'trainer' && roomIdRef.current) {
          socket.emit('clearGraphics', { roomId: roomIdRef.current });
          setStatus('Графика очищена');
        }
        didLongClear = true;
      }, 200);
    };

    const onDocumentMouseUp = () => {
      if (clearGraphicsTimer !== null) {
        window.clearTimeout(clearGraphicsTimer);
        clearGraphicsTimer = null;
      }
    };

    const onDocumentContextMenu = (event: MouseEvent) => {
      if (didLongClear) {
        event.preventDefault();
        didLongClear = false;
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('mouseup', onDocumentMouseUp);
    document.addEventListener('contextmenu', onDocumentContextMenu);

    return () => {
      window.clearInterval(timer);
      if (clearGraphicsTimer !== null) window.clearTimeout(clearGraphicsTimer);
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('mouseup', onDocumentMouseUp);
      document.removeEventListener('contextmenu', onDocumentContextMenu);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.removeAllListeners();
    };
  }, []);
}

